import * as React from 'react';
import { useEffect, useState, Suspense } from 'react';
import * as S from './styles';
import { DependencyManagerProvider } from 'src/contexts/dependencyManager';
import * as T from './types';
import { GlobalHeader } from 'src/components/shared-components/GlobalHeader';
import PrimaryWidgets from 'src/components/PrimaryWidgets';
import { ProgressIndicator } from '@veneer/core';
const { Shell } = window as any;
import { ShellProps } from 'src/types/shell';
import { I18nextProvider } from 'react-i18next';
import i18next from 'i18next';
import { initializeI18n } from 'src/utils/i18nUtils';
import { useTranslation } from 'react-i18next';
import { loadEnv, getEnv } from '../utils/env';
import { AnalyticsService } from '@mssi/analytics-ui-lib';
if (!customElements.get('veneer-skeleton')) {
  import('@veneer/skeleton-loading');
}

loadEnv();

declare global {
  interface Window {
    Shell: ShellProps;
  }
}

const AppComponent: React.FC<T.AppPropsType> = (properties) => {
  const { t } = useTranslation() as any;
  const businessModel = localStorage.getItem('businessModel') || 'MPS';
  const isMCSDashboard = businessModel === 'MCS' ? true : false;

  const isMDSDashboard = businessModel === 'MDS' ? true : false;

  const pageTitle = isMDSDashboard
    ? 'MDS Dashboard'
    : isMCSDashboard
      ? t('layout.dashboard', 'Dashboard')
      : t(
          'dashboard.customerDashboard.customerDashboard',
          'Customer Dashboard'
        );
  const userContext = JSON.parse(localStorage.getItem('userContext') || '{}');
  const [customerContext, setCustomerContext] = useState(
    JSON.parse(
      (userContext?.isPartnerUser
        ? sessionStorage.getItem('customerContext')
        : localStorage.getItem('customerContext')) || '{}'
    )
  );
  const [loading, setLoading] = useState(false);
  const [revealHeader, setRevealHeader] = useState(false);

  useEffect(() => {
    const GA_MEASUREMENT_ID = getEnv('GA_MEASUREMENT_ID');
    const initAnalytics = async () => {
      try {
        const gaCustomContext = JSON.parse(
          sessionStorage.getItem('ga-custom-context') || '{}'
        );

        const analytics = AnalyticsService.getInstance();
        await analytics.init(GA_MEASUREMENT_ID!, {
          ...gaCustomContext,
          event_category: 'Dashboard',
          event_sub_category: 'Customer Dashboard'
        });
      } catch (error) {
        console.error('Failed to initialize analytics:', error);
      }
    };

    initAnalytics();
  }, []);

  useEffect(() => {
    const handleCustomerChange = (event: any) => {
      setLoading(true);
      setCustomerContext(
        JSON.parse(
          (userContext?.isPartnerUser
            ? sessionStorage.getItem('customerContext')
            : localStorage.getItem('customerContext')) || '{}'
        )
      );
      setTimeout(() => {
        setLoading(false);
      }, 2000);
    };

    Shell.v1.events.addEventListener('customer-change', handleCustomerChange);

    return () => {
      Shell.v1.events.removeEventListener(
        'customer-change',
        handleCustomerChange
      );
    };
  }, []);

  // Listen for guided tour dashboard step event
  useEffect(() => {
    const handleGuidedTourDashboardStep = (eventData: any) => {
      console.log('Guided tour reached dashboard step:', eventData);
      // Reveal header by bringing it above Joyride's overlay
      setRevealHeader(true);
    };

    const handleGuidedTourDashboardEndStep = (eventData: any) => {
      console.log('Guided tour reached dashbaord step:', eventData);
      setRevealHeader(false);
    };

    Shell.v1.events.addEventListener(
      'shell-guided-tour-dashboard-step',
      handleGuidedTourDashboardStep
    );

    Shell.v1.events.addEventListener(
      'shell-guided-tour-dashboard-end-step',
      handleGuidedTourDashboardEndStep
    );

    return () => {
      Shell.v1.events.removeEventListener(
        'shell-guided-tour-dashboard-step',
        handleGuidedTourDashboardStep
      );
      Shell.v1.events.removeEventListener(
        'shell-guided-tour-dashboard-end-step',
        handleGuidedTourDashboardEndStep
      );
    };
  }, []);

  console.log('properties', properties);

  return (
    <>
      <S.StyledGlobalHeader revealHeader={revealHeader}>
        <GlobalHeader
          data-testid="device-component-global-header"
          title={pageTitle}
        />
      </S.StyledGlobalHeader>
      {loading ? (
        <ProgressIndicator
          appearance="linear"
          behavior="indeterminate"
        />
      ) : (
        <PrimaryWidgets
          navigation={properties.navigation}
          customerContext={customerContext}
          isMCSDashboard={isMCSDashboard}
          isMDSDashboard={isMDSDashboard}
          revealHeader={revealHeader}
        />
      )}
    </>
  );
};

const App: React.FC<T.AppPropsType> = (props) => {
  const { v1 } = window.Shell as ShellProps;
  const { store, events } = v1;
  const [isI18nInitialized, setIsI18nInitialized] = useState(false);
  const [locale, setLocale] = useState<string>(
    localStorage.getItem('locale') || 'en-US'
  );

  useEffect(() => {
    const initialize = async () => {
      await initializeI18n(locale, store);
      setIsI18nInitialized(true);
    };
    console.log('Initializing i18n with locale:', locale);

    initialize();
  }, [locale, store]);

  useEffect(() => {
    const localeChangedListener = (event: any) => {
      setLocale(event.eventData.locale);
    };
    events.addEventListener('Locale Changed', localeChangedListener);

    return () => {
      events.removeEventListener('Locale Changed', localeChangedListener);
    };
  }, []);

  if (!isI18nInitialized) {
    return null;
  }
  return (
    <DependencyManagerProvider>
      <I18nextProvider i18n={i18next}>
        <Suspense fallback={<div>Loading...</div>}>
          <AppComponent {...props} />
        </Suspense>
      </I18nextProvider>
    </DependencyManagerProvider>
  );
};

export default App;
